import {
  InstallBrowser,
  InstallEnvironment,
  InstallOperatingSystem,
} from './detectInstallEnvironment'

/**
 * Готовый текст инструкции установки для текущего окружения.
 */
export type PwaInstallGuide = {
  /** Краткая метка окружения, напр. «Windows · Google Chrome» */
  environmentLabel: string
  title: string
  introduction: string
  steps: string[]
  notes: string[]
}

const OPERATING_SYSTEM_LABEL: Record<InstallOperatingSystem, string> = {
  windows: 'Windows',
  macos: 'macOS',
  ios: 'iPhone / iPad',
  android: 'Android',
  linux: 'Linux',
  unknown: 'Ваша система',
}

const BROWSER_LABEL: Record<InstallBrowser, string> = {
  chrome: 'Google Chrome',
  edge: 'Microsoft Edge',
  safari: 'Safari',
  firefox: 'Firefox',
  samsung: 'Samsung Internet',
  opera: 'Opera',
  yandex: 'Яндекс.Браузер',
  unknown: 'текущий браузер',
}

/**
 * Собирает шаги установки под конкретную пару ОС + браузер.
 * @param environment — результат detectInstallEnvironment
 * @param hasNativeInstallPrompt — браузер отдал beforeinstallprompt
 */
export const getPwaInstallGuide = (
  environment: InstallEnvironment,
  hasNativeInstallPrompt = false
): PwaInstallGuide => {
  const environmentLabel = `${OPERATING_SYSTEM_LABEL[environment.operatingSystem]} · ${BROWSER_LABEL[environment.browser]}`

  if (environment.isStandalone) {
    return {
      environmentLabel,
      title: 'Приложение уже установлено',
      introduction:
        'Панель открыта как отдельное приложение. Дополнительная установка не нужна.',
      steps: [
        'Работайте из ярлыка на рабочем столе или с домашнего экрана.',
        'Чтобы получать уведомления о заказах в фоне, нажмите колокольчик в шапке и разрешите уведомления.',
      ],
      notes: [
        'Если уведомления не приходят, проверьте разрешения системы и браузера для bruno-pizza.',
      ],
    }
  }

  const { operatingSystem, browser } = environment

  if (operatingSystem === 'ios') {
    if (browser === 'safari' || browser === 'chrome' || browser === 'firefox' || browser === 'edge' || browser === 'opera' || browser === 'unknown') {
      return {
        environmentLabel,
        title: 'Установка на iPhone / iPad',
        introduction:
          'На iOS приложение ставится через меню «Поделиться». После установки откройте bruno-pizza с домашнего экрана — так работают фоновые уведомления.',
        steps: [
          'Откройте админку в Safari (если открыта в Chrome/другом браузере — скопируйте адрес и откройте в Safari: на iOS установка PWA надёжнее через Safari).',
          'Нажмите кнопку «Поделиться» (квадрат со стрелкой вверх) внизу или вверху экрана.',
          'Прокрутите список действий и выберите «На экран „Домой“» / «Add to Home Screen».',
          'Подтвердите имя «bruno-pizza» и нажмите «Добавить».',
          'Откройте ярлык с домашнего экрана и включите уведомления (иконка колокольчика).',
        ],
        notes: [
          'Установка только из вкладки браузера в режиме «на экране Домой» не равна ярлыку Safari — нужен именно пункт «На экран Домой».',
          'Web Push на iOS работает только после установки на домашний экран.',
        ],
      }
    }
  }

  if (operatingSystem === 'android') {
    if (browser === 'samsung') {
      return {
        environmentLabel,
        title: 'Установка в Samsung Internet',
        introduction:
          'Можно добавить панель как приложение или ярлык на домашний экран.',
        steps: hasNativeInstallPrompt
          ? [
              'Нажмите «Установить сейчас» в этом окне — откроется системный диалог.',
              'Подтвердите установку.',
              'Запускайте bruno-pizza с домашнего экрана и включите уведомления.',
            ]
          : [
              'Откройте меню браузера (☰ или ↓).',
              'Выберите «Добавить страницу на» → «Домашний экран» или «Установить приложение», если пункт есть.',
              'Подтвердите добавление.',
              'Откройте ярлык и разрешите уведомления через колокольчик в шапке.',
            ],
        notes: [
          'Если пункта установки нет, обновите Samsung Internet и откройте сайт по HTTPS.',
        ],
      }
    }

    return {
      environmentLabel,
      title: 'Установка на Android',
      introduction:
        'В Chrome и большинстве Chromium-браузеров панель можно установить как приложение.',
      steps: hasNativeInstallPrompt
        ? [
            'Нажмите «Установить сейчас» ниже — появится системный запрос.',
            'Подтвердите установку bruno-pizza.',
            'Откройте приложение с домашнего экрана и включите уведомления (колокольчик).',
          ]
        : [
            'Нажмите меню ⋮ справа вверху.',
            'Выберите «Установить приложение» / «Install app» или «Добавить на главный экран».',
            'Подтвердите установку.',
            'Запустите ярлык и разрешите уведомления.',
          ],
      notes: [
        'В Firefox на Android поддержка установки PWA ограничена — предпочтительнее Chrome.',
      ],
    }
  }

  if (operatingSystem === 'windows') {
    if (browser === 'edge' || browser === 'chrome' || browser === 'yandex' || browser === 'opera') {
      return {
        environmentLabel,
        title: 'Установка на Windows',
        introduction:
          'Панель ставится как обычное окно приложения с ярлыком в меню «Пуск» и на рабочем столе.',
        steps: hasNativeInstallPrompt
          ? [
              'Нажмите «Установить сейчас» в этом окне.',
              'Подтвердите установку в диалоге браузера.',
              'Запускайте bruno-pizza из меню «Пуск» или с рабочего стола.',
              'Включите уведомления колокольчиком в шапке, чтобы слышать новые заказы даже при свёрнутом окне.',
            ]
          : [
              'Посмотрите вправо в адресной строке — иконка монитора / «Установить приложение» (⊕ / install).',
              'Либо откройте меню ⋮ → «Установить bruno-pizza…» / «Cast, save, and share» → «Install page as app» (Chrome) / «Apps» → «Install this site as an app» (Edge).',
              'Подтвердите установку.',
              'Откройте установленное приложение и разрешите уведомления.',
            ],
        notes: [
          'Если пункта установки нет: сайт должен быть на HTTPS, service worker активен, откройте админку не в режиме инкогнито.',
        ],
      }
    }
    if (browser === 'firefox') {
      return {
        environmentLabel,
        title: 'Firefox на Windows',
        introduction:
          'В Firefox установка PWA как отдельного приложения почти не поддерживается.',
        steps: [
          'Откройте эту же ссылку в Google Chrome или Microsoft Edge.',
          'В Chrome/Edge установите панель как приложение (иконка в адресной строке или меню «Установить»).',
          'Для уведомлений о заказах пользуйтесь установленным приложением Chrome/Edge.',
        ],
        notes: [
          'Можно оставить вкладку Firefox открытой, но фоновый Web Push и «как приложение» удобнее в Chrome или Edge.',
        ],
      }
    }
  }

  if (operatingSystem === 'macos') {
    if (browser === 'safari') {
      return {
        environmentLabel,
        title: 'Установка в Safari на Mac',
        introduction:
          'В современных macOS Safari умеет добавлять сайт в Dock как приложение.',
        steps: [
          'В меню Safari выберите «Файл» → «Добавить в Dock…» (или «Add to Dock»).',
          'Если пункта нет: «Поделиться» → «Добавить на рабочий стол» / откройте сайт в Chrome и установите оттуда.',
          'Запустите ярлык из Dock.',
          'Включите уведомления через колокольчик в шапке.',
        ],
        notes: [
          'На старых macOS надёжнее установить через Google Chrome («Установить приложение» в адресной строке).',
        ],
      }
    }
    if (browser === 'chrome' || browser === 'edge' || browser === 'yandex' || browser === 'opera') {
      return {
        environmentLabel,
        title: 'Установка на macOS',
        introduction: 'Chromium-браузеры ставят панель как отдельное приложение.',
        steps: hasNativeInstallPrompt
          ? [
              'Нажмите «Установить сейчас».',
              'Подтвердите установку.',
              'Запускайте приложение из Launchpad / Applications и включите уведомления.',
            ]
          : [
              'В адресной строке нажмите иконку установки (компьютер) или меню ⋮ → «Установить bruno-pizza…».',
              'Подтвердите установку.',
              'Откройте приложение и разрешите уведомления.',
            ],
        notes: [],
      }
    }
    if (browser === 'firefox') {
      return {
        environmentLabel,
        title: 'Firefox на macOS',
        introduction: 'Для установки как приложения откройте админку в Chrome или Edge.',
        steps: [
          'Скопируйте адрес админки.',
          'Откройте его в Google Chrome или Microsoft Edge.',
          'Установите через иконку в адресной строке или меню браузера.',
        ],
        notes: [],
      }
    }
  }

  if (operatingSystem === 'linux') {
    if (browser === 'chrome' || browser === 'edge' || browser === 'opera' || browser === 'yandex') {
      return {
        environmentLabel,
        title: 'Установка на Linux',
        introduction: 'В Chromium-браузерах доступна установка сайта как приложения.',
        steps: hasNativeInstallPrompt
          ? [
              'Нажмите «Установить сейчас».',
              'Подтвердите установку в диалоге.',
              'Запускайте приложение из меню системы и включите уведомления.',
            ]
          : [
              'Меню ⋮ → «Установить bruno-pizza…» / иконка установки в адресной строке.',
              'Подтвердите установку.',
              'Включите уведомления колокольчиком.',
            ],
        notes: ['В Firefox на Linux установка PWA обычно недоступна — используйте Chrome.'],
      }
    }
  }

  // Универсальный fallback
  return {
    environmentLabel,
    title: 'Как установить панель как приложение',
    introduction:
      'Инструкция подобрана по общим правилам. Если шаги не совпадают — используйте Chrome или Edge.',
    steps: hasNativeInstallPrompt
      ? [
          'Нажмите «Установить сейчас» ниже.',
          'Подтвердите установку.',
          'Откройте установленное приложение и разрешите уведомления.',
        ]
      : [
          'Откройте админку в Google Chrome или Microsoft Edge по HTTPS.',
          'В адресной строке или меню браузера найдите «Установить приложение» / «Install app».',
          'На телефоне Apple: Safari → «Поделиться» → «На экран Домой».',
          'После установки включите уведомления колокольчиком в шапке.',
        ],
    notes: [
      'Установка недоступна в режиме инкогнито и без HTTPS.',
      `Определено окружение: ${environmentLabel}.`,
    ],
  }
}
